import { ReactNode, useEffect, useState } from 'react';

import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';

import {
  extractBodyContent,
  fixTridionImageSources,
} from '@/utils/document-utils';

import { SafeImg } from '@/components/SafeImage';

import Constants from '@/constants';

import PdfContent from '../../tridion-components/PdfContent';
import TridionGameContent from '../../tridion-components/TridionGameContent';

import { SearchDocument } from '@/_generated/api-clients/xp';
import { Games, Video } from '@/icons';

const documentCache = new Map<string, string>();

interface UseTridionDocumentOptions {
  documentType?: SearchDocument['type'];
  documentName?: string | null;
  fallbackVideoUrl?: string;
  icon?: string;
}

interface TridionTypeConfig {
  iconContent: ReactNode;
  summary: string | null;
  documentContent: ReactNode;
}
export const useTridion = (
  url?: string,
  options?: UseTridionDocumentOptions,
): { htmlContent: string | undefined; error: boolean } & TridionTypeConfig => {
  const { t } = useTranslation();
  const [htmlContent, setHtmlContent] = useState<string>();
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!url) {
      return;
    }

    (async () => {
      if (documentCache.has(url)) {
        setHtmlContent(documentCache.get(url));
        return;
      }

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        console.error(
          `Tridion document failed to fetch: ${response.status} ${response.statusText}`,
        );
        setError(true);
      }

      const html = await response.text();
      setHtmlContent(response.ok ? extractBodyContent(html) : html);
      if (response.ok) {
        documentCache.set(url, extractBodyContent(html));
      }
    })();
  }, [url]);

  const contentFixedSrc = fixTridionImageSources(htmlContent);

  // Extract iframe src from HTML content if present, otherwise fall back to fallbackVideoUrl
  const resolvedVideoUrl = (() => {
    if (contentFixedSrc) {
      const iframeMatch = /<iframe[^>]+src=["']([^"']+)["']/i.exec(
        contentFixedSrc,
      );
      if (iframeMatch?.[1]) {
        return iframeMatch[1];
      }
    }
    return options?.fallbackVideoUrl;
  })();

  const defaultIcon = (
    <SafeImg
      className="size-[56px] object-contain"
      alt="document icon"
      src={`${Constants.PATHWAY_CDN_IMG.PROCEDURES}${
        options?.icon === undefined || options.icon === 'placeholder'
          ? 'Chickfila'
          : options.icon
      }.svg`}
      fallback={`${Constants.PATHWAY_CDN_IMG.PROCEDURES}Chickfila.svg`}
    />
  );

  const typeConfig = ((): Partial<TridionTypeConfig> | null => {
    switch (options?.documentType) {
      case 'VIDEO':
        return {
          iconContent: (
            <Video
              className="size-14"
              role="img"
              aria-label={t('video.icon.label')}
            />
          ),
          documentContent: (
            <iframe
              className="aspect-video w-full"
              allowFullScreen
              src={resolvedVideoUrl}
              title={options.documentName ?? 'Video content'}
            />
          ),
        };

      case 'GAME':
        return {
          iconContent: (
            <Games
              className="size-14"
              role="img"
              aria-label={t('games.iconLabel')}
            />
          ),
          documentContent: <TridionGameContent htmlContent={htmlContent} />,
        };

      case 'PDF':
        return {
          documentContent: <PdfContent htmlContent={htmlContent} />,
        };
    }
    return null;
  })();

  const parsedContent = contentFixedSrc ? parse(contentFixedSrc) : null;

  return {
    htmlContent: contentFixedSrc,
    error,
    iconContent: typeConfig?.iconContent ?? defaultIcon,
    summary: typeConfig?.summary ?? null,
    documentContent: typeConfig?.documentContent ?? parsedContent,
  };
};
